import {
  AudioRecorder,
  MicrophoneSelector,
  PromptSelect,
  TemplateSelect,
} from '@repo/videa-notes';

import {
  useRecorderState,
  useRecordingHandlers,
  useTemplateSelection,
  useVideaNotesCoreContext,
} from '../../context';

/**
 * Audio capture header component.
 * Shows microphone selector and prompt/template selection in the header area.
 */
export const AudioCaptureHeader = () => {
  const { isVoiceNotesCustomTemplatesEnabled } = useVideaNotesCoreContext();

  const { recorderStatus, mediaBlobUrl } = useRecorderState();

  const {
    promptOptions,
    selectedPrompt,
    templateOptions,
    selectedTemplate,
    onSelectedPrompt,
    onSelectedTemplate,
  } = useTemplateSelection();

  const { onMonitoringStreamChange } = useRecordingHandlers();

  const isRecorderStopped =
    recorderStatus === 'stopped' || recorderStatus === 'idle';

  // Don't show header controls if we have a recorded blob
  if (mediaBlobUrl) {
    return null;
  }

  return (
    <div className="vai-flex vai-justify-end vai-w-full vai-gap-1">
      <div className="vai-w-1/2 vai-pr-1">
        <MicrophoneSelector
          onMonitoringStreamChange={onMonitoringStreamChange}
        />
      </div>
      {isRecorderStopped && (
        <>
          {!isVoiceNotesCustomTemplatesEnabled && (
            <div className="vai-w-1/2 vai-pr-1">
              <PromptSelect
                promptOptions={promptOptions}
                selectedPrompt={selectedPrompt}
                onSelectedPrompt={onSelectedPrompt}
              />
            </div>
          )}
          {isVoiceNotesCustomTemplatesEnabled && (
            <div className="vai-w-1/2 vai-pr-1">
              <TemplateSelect
                templateOptions={templateOptions}
                selectedTemplate={selectedTemplate}
                onSelectedTemplate={onSelectedTemplate}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

/**
 * Audio capture body component.
 * Shows the audio recorder controls.
 */
export const AudioCaptureBody = () => {
  const { patient, patientName, isVoiceNotesConsentEnabled } =
    useVideaNotesCoreContext();

  const { selectedPrompt, isAudioUploadEnabled, showDevTools } =
    useTemplateSelection();

  const {
    onCancelRecording,
    onCancelRecordingConfirmation,
    onPauseRecording,
    onResumeRecording,
    onStartRecording,
    onStartProviderOnlyRecording,
    onGenerateNote,
    onPatientConsentDenied,
  } = useRecordingHandlers();

  return (
    <div className="vai-flex vai-flex-col vai-items-center vai-py-2 vai-w-full">
      <AudioRecorder
        patientName={patientName}
        patientId={patient?.pmsPatientId ?? 'N/A'}
        isPromptSelected={selectedPrompt !== undefined}
        isVoiceNotesConsentEnabled={isVoiceNotesConsentEnabled}
        isAudioUploadEnabled={isAudioUploadEnabled}
        showDevTools={showDevTools}
        onCancelRecording={onCancelRecording}
        onCancelRecordingConfirmation={onCancelRecordingConfirmation}
        onPauseRecording={onPauseRecording}
        onResumeRecording={onResumeRecording}
        onStartRecording={onStartRecording}
        onStartProviderOnlyRecording={onStartProviderOnlyRecording}
        onGenerateNote={onGenerateNote}
        onPatientConsentDenied={onPatientConsentDenied}
      />
    </div>
  );
};
